import {
  CadModelObject,
  GaussianSplatsObject,
  IElementObject,
  MeshObject,
  PanoObject,
  PhotoObject,
  PointCloudObject,
  SheetObject,
} from "@/object-cache";
import { isIElementValidPointCloudStream } from "@/types/type-guards";
import {
  IElement,
  isIElementGaussianSplats,
  isIElementGenericImgSheet,
  isIElementGenericModel3d,
  isIElementImg2d,
  isIElementImg360,
  validateIElement,
} from "@faro-lotv/ielement-types";
import { FloorPlanTile, isCadModel } from "@faro-lotv/lotv";
import { Object3D } from "three";
import { isIElementOrthomosaic } from "./types/ielement-extra-types";

export type UnknownObject = IElementObject<Object3D, IElement>;

/**
 * @returns True if the input object is a PanoObject
 * @param o The object to check
 */
export function isPanoObject(o: UnknownObject | null): o is PanoObject {
  return !!o && isIElementImg360(o.iElement);
}

/**
 * @returns True if the input object is a PhotoObject
 * @param o The object to check
 */
export function isPhotoObject(o: UnknownObject | null): o is PhotoObject {
  return !!o && isIElementImg2d(o.iElement);
}

/**
 * @returns True if the input object is a PointCloudObject
 * @param o The object to check
 */
export function isPointCloudObject(
  o: UnknownObject | null,
): o is PointCloudObject {
  return !!o && isIElementValidPointCloudStream(o.iElement);
}

/**
 * @returns True if the input object is a GaussianSplatsObject
 * @param o The object to check
 */
export function isGaussianSplatsObject(
  o: UnknownObject | null,
): o is GaussianSplatsObject {
  return !!o && isIElementGaussianSplats(o.iElement);
}

/**
 * @returns True if the input object is a MeshObject
 * @param o The object to check
 */
export function isMeshObject(o: UnknownObject | null): o is MeshObject {
  return !!o && isIElementGenericModel3d(o.iElement);
}

/**
 * @returns True if the input object is a CadModelObject
 * @param o The object to check
 */
export function isCadModelObject(
  o: UnknownObject | null | undefined,
): o is CadModelObject {
  return !!o && isCadModel(o) && isIElementGenericModel3d(o.iElement);
}

/**
 * @returns True if the input object is a SheetObject
 * @param o The object to check
 */
export function isSheetObject(o: Object3D | null): o is SheetObject {
  return isUnknownObject(o) && isIElementGenericImgSheet(o.iElement);
}

/**
 * WARNING: this function will return true also for any tiled sheet objects,
 * until we do not have a proper type hint to flag orthomosaics
 *
 * @returns True if the input object is a SheetObject
 * @param o The object to check
 */
export function isOrthomosaicObject(o: UnknownObject | null): o is SheetObject {
  return !!o && isIElementOrthomosaic(o.iElement);
}

/**
 * @returns True iff the argument object is an UnknownObject
 * @param o The object to check
 */
export function isUnknownObject(o: Object3D | null): o is UnknownObject {
  return !!o && "iElement" in o && validateIElement(o.iElement);
}

/**
 * @param o The object to check
 * @returns true if it's a sheet object or a tile of a sheet object
 */
export function isFromSheetObject(o: Object3D | null): boolean {
  return isSheetObject(o) || o instanceof FloorPlanTile;
}
